'use client';
import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { createInvite, buildInviteUrl } from '@/services/inviteService';
import { track, ACTIVATION_EVENTS } from '@/lib/analytics';
import toast from 'react-hot-toast';
import {
  UsersIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ShareIcon,
  EnvelopeIcon,
  ChatBubbleOvalLeftEllipsisIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

const PARENT_INVITE_MAX_USES = 50;
const PARENT_INVITE_EXPIRES_DAYS = 365;

export default function ParentInviteCard({ teamId, uid, canManage = true, teamName = '' }) {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [invite, setInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!teamId) return;
    (async () => {
      try {
        const q = query(
          collection(db, 'invites'),
          where('teamId', '==', teamId),
          where('audience', '==', 'parent'),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        if (!snap.empty) {
          const d = snap.docs[0];
          setInvite({ id: d.id, ...d.data() });
        }
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[ParentInviteCard] fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  const url = invite ? buildInviteUrl(invite.code, teamId) : '';

  const handleCreate = async () => {
    if (!uid || !teamId || creating) return;
    setCreating(true);
    const tid = toast.loading('Creating parent invite link…');
    try {
      const { code } = await createInvite({
        teamId,
        teamName: teamName || null,
        createdBy: uid,
        audience: 'parent',
        preset: 'parent',
        maxUses: PARENT_INVITE_MAX_USES,
        expiresInDays: PARENT_INVITE_EXPIRES_DAYS,
        label: 'Parent invite link',
        metadata: { createdVia: 'parent-invite-card' },
      });
      setInvite({
        code,
        teamId,
        audience: 'parent',
        status: 'active',
        maxUses: PARENT_INVITE_MAX_USES,
        usedCount: 0,
      });
      toast.success('Parent invite link ready', { id: tid });
    } catch (err) {
      toast.error(err?.message || 'Failed to create invite', { id: tid });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  const shareText = teamName
    ? `Join ${teamName} on Scrimly: ${url}`
    : `Join our team on Scrimly: ${url}`;

  const handleNativeShare = async () => {
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join the team on Scrimly', text: shareText, url });
      } catch {
      }
    } else {
      handleCopy();
    }
  };

  const usesLeft = invite
    ? Math.max(0, (invite.maxUses || PARENT_INVITE_MAX_USES) - (invite.usedCount || 0))
    : null;

  return (
    <section className="rounded-2xl border border-[#b5f03d]/40 bg-gradient-to-br from-[#b5f03d]/12 to-transparent p-5 sm:p-6 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/25">
          <UsersIcon className="h-5 w-5 text-[#3d6010]" />
        </div>
        <div className="min-w-0 flex-1">
          <h2
            className="uppercase text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20, letterSpacing: '0.5px' }}
          >
            Parent invite link
          </h2>
          <p className="text-[13px] text-zinc-600 mt-0.5">
            One reusable link. Parents tap it, sign up, and they're connected to the team.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="h-12 rounded-[10px] bg-zinc-100 animate-pulse" />
      ) : !invite ? (
        canManage ? (
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 min-h-[52px] uppercase transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 16, letterSpacing: '0.6px' }}
          >
            {creating ? 'Creating…' : 'Generate parent invite link'}
          </button>
        ) : (
          <p className="text-sm text-zinc-500">Only team admins can create invite links.</p>
        )
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-h-[52px] rounded-[10px] border border-zinc-300 bg-white px-4 py-3.5 text-[14px] text-[#0d1f14] focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 min-h-[52px] uppercase transition-colors"
              style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 14, letterSpacing: '0.6px' }}
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleNativeShare}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors"
            >
              <ShareIcon className="h-4 w-4" /> Share
            </button>
            <a
              href={`sms:?body=${encodeURIComponent(shareText)}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors"
            >
              <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4" /> SMS
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent('Join the team on Scrimly')}&body=${encodeURIComponent(shareText)}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors"
            >
              <EnvelopeIcon className="h-4 w-4" /> Email
            </a>
            {canManage && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Generate a new link? The current one will keep working until it expires.')) {
                    track(ACTIVATION_EVENTS.STAFF_INVITED, { team_id: teamId, audience: 'parent', regenerated: true });
                    setInvite(null);
                    handleCreate();
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" /> Regenerate
              </button>
            )}
          </div>

          {usesLeft !== null && (
            <p className="mt-3 text-[12px] text-zinc-500">
              {usesLeft} of {invite.maxUses || PARENT_INVITE_MAX_USES} uses remaining · expires in 1 year
            </p>
          )}
        </>
      )}
    </section>
  );
}
